import { DEFAULT_CURRENCY, ContractDemandDetails, contractDemandDetailsSchema } from '@procureai/schemas';
import { UseEditableWidgetBackend } from '@procureai/ui-modules-mui';

import { Currency } from '@/generated/gql/graphql';
import { createEditableWidgetGraphqlBackendHook } from '@/shared/hooks/useEditableWidgetGraphqlBackend/useEditableWidgetGraphqlBackend';
import { isContractDemandEditable } from '~/common/guards/contractDemand.guards';
import { useModuleContext } from '~/components/ModuleContextProvider';

import { CONTRACT_DEMAND_UPDATE_MUTATION } from './ContractDemandDetailWidget.mutation.gql';
import { CONTRACT_DEMAND_DETAIL_QUERY } from './ContractDemandDetailWidget.query.gql';

const useContractDetailWidgetBackendInternal = createEditableWidgetGraphqlBackendHook({
  schema: contractDemandDetailsSchema,
  query: CONTRACT_DEMAND_DETAIL_QUERY,
  mutation: CONTRACT_DEMAND_UPDATE_MUTATION,
});

export const useContractDetailWidgetBackend: UseEditableWidgetBackend<ContractDemandDetails> = ({ i18n }) => {
  const { entityId } = useModuleContext();
  const variables = { id: entityId };

  return useContractDetailWidgetBackendInternal({
    i18n,
    variables,
    skip: !entityId,
    toFormData: (data) => {
      const contractDemand = data?.tesseract.contractDemand;
      const contractDemandRequest = contractDemand?.demandRequest;
      const { validityPeriod } = contractDemand?.demandRequest || {};
      return {
        commodityGroup: contractDemandRequest?.commodityGroupId,
        currency: (contractDemandRequest?.currency ?? DEFAULT_CURRENCY) as ContractDemandDetails['currency'],
        tags: contractDemand?.tagIds || [],
        incoTerms: contractDemandRequest?.incoTermsId,
        paymentTerms: contractDemandRequest?.paymentTermsId,
        description: contractDemandRequest?.summary ?? '',
        validityPeriod: [new Date(validityPeriod?.from), new Date(validityPeriod?.to)],
        responsible: contractDemand?.responsibleUserId,
      };
    },
    toMutationData: (formData) => {
      return {
        id: entityId,
        contractDemandData: {
          responsible: formData.responsible!,
          tags: formData.tags || [],
          commodityGroup: formData.commodityGroup,
          incoTerms: formData.incoTerms,
          paymentTerms: formData.paymentTerms,
          currency: formData.currency as Currency,
          validFrom: formData.validityPeriod[0]!,
          validTo: formData.validityPeriod[1]!,
          summary: formData.description || '',
        },
      };
    },
    toSimpleFormWidgetProps(data, loading) {
      return loading || !isContractDemandEditable(data?.tesseract.contractDemand)
        ? { disabled: true }
        : { disabled: false };
    },
  });
};
